/**
 * API client.
 *
 * Holds the access token in memory only — never localStorage. A token in
 * localStorage is readable by any injected script; the refresh cookie is
 * HttpOnly, so a page reload recovers the session without that exposure.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? ''

/**
 * Resolves an API-relative media path (`/publico/midia/...`) into an absolute
 * URL. The panel lives on a different domain than the API — a relative src in
 * an <img> resolves against the PANEL and 404s (the broken-logo bug).
 */
export function urlMidia(caminho: string | null | undefined): string | null {
  if (!caminho) return null
  return caminho.startsWith('/') ? `${BASE}${caminho}` : caminho
}

let accessToken: string | null = null
let aoExpirar: (() => void) | null = null

export function definirToken(token: string | null): void {
  accessToken = token
}

export function obterToken(): string | null {
  return accessToken
}

export function aoPerderSessao(callback: () => void): void {
  aoExpirar = callback
}

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly codigo?: string,
  ) {
    super(mensagem)
    this.name = 'ErroApi'
  }
}

interface OpcoesRequisicao {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  corpo?: unknown
  /** Set to false on the refresh call itself, to avoid infinite recursion. */
  tentarRenovar?: boolean
}

/**
 * Routes that authenticate from the request itself, never from a stored token.
 *
 * Attaching the old token to a login call is how a browser holding a restricted
 * (pre-MFA) session got 403 on correct credentials: the server saw the stale
 * token, decided the session was incomplete, and refused before reading the
 * body. The server no longer does that — but sending a credential that means
 * nothing to the route is pointless either way, and the two fixes together mean
 * a stale session can never block a fresh login again.
 */
const ROTAS_ANONIMAS = new Set(['/api/v1/auth/login', '/api/v1/auth/renovar'])

async function executar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const { metodo = 'GET', corpo, tentarRenovar = true } = opcoes

  const anonima = ROTAS_ANONIMAS.has(caminho)

  const resposta = await fetch(`${BASE}${caminho}`, {
    method: metodo,
    headers: {
      ...(corpo ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken && !anonima ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    // Necessário para o cookie HttpOnly de refresh viajar.
    credentials: 'include',
    body: corpo ? JSON.stringify(corpo) : undefined,
  })

  // Access token expirou: tenta renovar uma vez e repete a chamada original.
  if (resposta.status === 401 && tentarRenovar && accessToken) {
    const renovou = await renovarSessao()
    if (renovou) return executar<T>(caminho, { ...opcoes, tentarRenovar: false })
    accessToken = null
    aoExpirar?.()
  }

  if (resposta.status === 204) return undefined as T

  const dados = await resposta.json().catch(() => ({}))

  if (!resposta.ok) {
    const erro = (dados as { erro?: { mensagem?: string; codigo?: string } }).erro
    throw new ErroApi(
      resposta.status,
      erro?.mensagem ?? `Falha na requisição (${resposta.status}).`,
      erro?.codigo,
    )
  }

  return dados as T
}

async function renovarSessao(): Promise<boolean> {
  try {
    const r = await fetch(`${BASE}/api/v1/auth/renovar`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!r.ok) return false
    const dados = (await r.json()) as { accessToken?: string }
    if (!dados.accessToken) return false
    accessToken = dados.accessToken
    return true
  } catch {
    return false
  }
}

/**
 * Uploads a file.
 *
 * Separate from `executar` because multipart must NOT carry a JSON
 * content-type: the browser has to set the boundary itself, and a hand-written
 * header breaks the parse on the server with an error that reads like a bug in
 * the file.
 *
 * Uses the same in-memory token as every other call. Reading it from
 * localStorage — which is where an upload helper naturally reaches — would both
 * send nothing (it is not there) and undo the reason it is not there.
 */
async function enviarArquivo<T>(
  caminho: string,
  arquivo: File,
  campo = 'arquivo',
): Promise<T> {
  const corpo = new FormData()
  corpo.append(campo, arquivo)

  const chamar = async (): Promise<Response> =>
    fetch(`${BASE}${caminho}`, {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'include',
      body: corpo,
    })

  let resposta = await chamar()

  if (resposta.status === 401 && accessToken) {
    if (await renovarSessao()) resposta = await chamar()
  }

  const dados = await resposta.json().catch(() => ({}))
  if (!resposta.ok) {
    const erro = (dados as { erro?: { mensagem?: string; codigo?: string } }).erro
    throw new ErroApi(
      resposta.status,
      erro?.mensagem ?? `Falha no envio (${resposta.status}).`,
      erro?.codigo,
    )
  }
  return dados as T
}

/**
 * Busca uma imagem que exige sessão e devolve uma URL de objeto.
 *
 * `<img src>` não manda o cabeçalho `Authorization`, então uma foto protegida
 * não pode ser carregada pelo caminho normal. A alternativa seria servi-la numa
 * rota pública — e foto de pessoa não vai para rota pública. Devolve `null`
 * quando não há foto, que é o caso comum e não é erro.
 *
 * Quem chama é dono da URL: `URL.revokeObjectURL` na limpeza, senão cada
 * remontagem vaza alguns megabytes.
 */
async function buscarImagem(caminho: string): Promise<string | null> {
  const chamar = async (): Promise<Response> =>
    fetch(`${BASE}${caminho}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'include',
    })

  let resposta = await chamar()
  if (resposta.status === 401 && accessToken) {
    if (await renovarSessao()) resposta = await chamar()
  }
  if (!resposta.ok) return null

  return URL.createObjectURL(await resposta.blob())
}

export const api = {
  get: <T>(caminho: string) => executar<T>(caminho),
  enviarArquivo,
  buscarImagem,
  post: <T>(caminho: string, corpo?: unknown) => executar<T>(caminho, { metodo: 'POST', corpo }),
  put: <T>(caminho: string, corpo?: unknown) => executar<T>(caminho, { metodo: 'PUT', corpo }),
  patch: <T>(caminho: string, corpo?: unknown) => executar<T>(caminho, { metodo: 'PATCH', corpo }),
  delete: <T>(caminho: string) => executar<T>(caminho, { metodo: 'DELETE' }),
  renovarSessao,
}

// ---------------------------------------------------------------------------
// Tipos das respostas
// ---------------------------------------------------------------------------

export interface Usuario {
  id: string
  nome: string
  email: string
  papel: string
  organizacaoId: string | null
  mfaAtivo: boolean
  mfaObrigatorio?: boolean
  /** Verticais visíveis no painel: escolas, companhias ou ambas. */
  verticais?: Array<'escolas' | 'companhias'>
}

export interface RespostaLogin {
  accessToken: string
  expiraEmSegundos: number
  usuario: Usuario
  precisaCadastrarMfa: boolean
}

export interface Categoria {
  id: string
  slug: string
  nome: string
  descricao: string | null
  fundamentoLegal: string | null
  urlFundamento: string | null
  publicoAlvo: Record<string, unknown>
  schemaIntake: Record<string, unknown>
  regraValidacao: Record<string, unknown>
  sazonalidade: Record<string, unknown>
  exigeRegularidade: boolean
  slaPublicacaoDias: number
  ativa: boolean
  ordem: number
  atualizadoEm: string
}

export interface RegraPreco {
  modo: 'cm_coluna' | 'lauda' | 'caractere' | 'palavra' | 'linha' | 'tabela_faixa'
  moeda: 'BRL'
  valorUnitarioCentavos?: number
  faixas?: Array<{ ate: number | null; valorCentavos: number }>
  quantidadeMinima?: number
  valorMinimoCentavos?: number
  valorMaximoCentavos?: number
  arredondamento?: 'cima' | 'normal' | 'baixo'
  acrescimos?: Array<{
    codigo: string
    descricao: string
    percentual?: number
    valorCentavos?: number
  }>
  descontosVolume?: Array<{ aPartirDe: number; percentual: number }>
  percentualTributos?: number
}

export interface TabelaPreco {
  id: string
  categoriaId: string
  categoriaNome?: string | null
  descricao: string
  regraPreco: RegraPreco
  vigenciaInicio: string
  vigenciaFim: string | null
  atoAprovacao: string | null
  ativa: boolean
  criadoEm: string
}

export interface Configuracao {
  id: string
  chave: string
  valor: unknown
  grupo: string
  rotulo: string
  descricao: string | null
  schemaValor: Record<string, unknown> | null
  sensivel: boolean
  atualizadoEm: string
}

export interface EventoAuditoria {
  id: string
  ator: string | null
  acao: string
  entidade: string
  entidadeId: string | null
  ocorridoEm: string
}

export interface EstadoEleitoral {
  /** True quando alguém desligou o gate à mão dentro do período vedado. */
  gateDesligadoNaJanela?: boolean
  janelaPorCalendario?: boolean
  janelaAtiva: boolean
  configuracaoValida: boolean
  janelaInicio: string
  janelaFim: string
  modo: string
  excecaoMercadoHabilitada: boolean
  fundamentoLegal: string
}

export interface Lista<T> {
  dados: T[]
  total: number
}

export interface Item<T> {
  dados: T
  mensagem?: string
}
